import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { Charts } from '@/_shared/components/Charts';
import { ReadMoreTypography } from '@/_shared/components/ReadMoreTypography/ReadMoreTypography';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { ChipsTemplate } from '@/surveys/components';
import { getTemplatesCall, postEmployerByIdSurveyResultCall } from '@/surveys/state';
import { ResultsNotVisible } from '../ResultsNotVisible';

export const SurveyResultsManager = () => {
  const t = useTranslations('dashboard.learning.surveyResults');
  const { genericResponsiveValue } = useResponsiveStyle();

  const [activeChip, setActiveChip] = useState<string>('');

  const { response: responsePostEmployerByIdSurveyResult } =
    postEmployerByIdSurveyResultCall.hook();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const hasMoreThanOneTemplate = useMemo(() => {
    return (
      new Set(
        responsePostEmployerByIdSurveyResult
          ?.filter(survey => survey.completed === true)
          .map(survey => survey.templateId) ?? [],
      ).size > 1
    );
  }, [responsePostEmployerByIdSurveyResult]);

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  useEffect(() => {
    if (!hasMoreThanOneTemplate && responsePostEmployerByIdSurveyResult) {
      const { templateName } =
        responsePostEmployerByIdSurveyResult.find(survey => survey.completed === true) || {};
      if (templateName) {
        setActiveChip(templateName);
      }
    }
  }, [hasMoreThanOneTemplate, responsePostEmployerByIdSurveyResult]);

  return (
    <Box>
      <Box>
        <Typography mb='xs' variant='h2'>
          {t('title.moreThanOne')}
        </Typography>
        {hasMoreThanOneTemplate && (
          <ChipsTemplate setActiveChip={setActiveChip} activeChip={activeChip} />
        )}
      </Box>
      {responsePostEmployerByIdSurveyResult
        ?.filter(survey => survey.templateName === activeChip && survey.completed === true)
        .map(survey =>
          survey.canViewResults === false ? (
            <ResultsNotVisible
              surveyTemplateName={survey.templateName}
              key={`${survey.templateId}${survey.templateVersion}`}
            />
          ) : (
            <Box
              key={`${survey.templateId}${survey.templateVersion}`}
              display='flex'
              flexDirection='column'
              mb='l'
            >
              <Box flex='1'>
                <Typography mb='xs' variant='h3'>
                  {t('title.single', { surveyTemplateName: survey.templateName })}
                </Typography>
                <Typography variant='p3/light' mb='m'>
                  {t('description', {
                    metricsCount:
                      (survey &&
                        survey.results &&
                        survey.results.results &&
                        survey.results?.results.length) ??
                      0,
                    surveyTemplateName: survey.templateName,
                  })}
                </Typography>
                <Charts.RadarChartComponent
                  data={(survey.results?.results ?? []).map(metric => {
                    return {
                      subject: metric.metric?.name ?? '',
                      current: metric.score ?? 0,
                      expected: 7,
                      description: getDescriptionMetric(
                        survey.templateId || '',
                        metric.metric?.name || '',
                      ),
                    };
                  })}
                />
              </Box>
              <Card
                mt='m'
                display='flex'
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'column' },
                  defaultValue: 'column',
                })}
                flex='1'
              >
                <Box flex='1'>
                  <Typography variant='h4'>{t('insightBox.title')}</Typography>
                  <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                    <ReadMoreTypography variant='p3/light'>
                      {Object.keys(survey.results?.insights ?? {}).map(key => (
                        <Typography variant='p3/label' key={key}>
                          {key} -{' '}
                          <Typography as='span' variant='p3/light'>
                            {(survey.results?.insights as Record<string, string>)[key]}
                          </Typography>
                          {'\n'}
                        </Typography>
                      ))}
                    </ReadMoreTypography>
                  </Box>
                </Box>
                <Box
                  display='flex'
                  flexDirection={genericResponsiveValue({
                    breakPoints: { desktop: 'row' },
                    defaultValue: 'column',
                  })}
                  justifyContent='space-between'
                >
                  <Box flex='1'>
                    <Typography variant='h4'>{t('insightBox.proTitle')}</Typography>
                    <Box display='flex' flexDirection='column' mt='xs'>
                      {survey.results?.results?.map(item => {
                        const maxScore = Math.max(
                          ...(survey.results?.results ?? []).map(m => m.score ?? 0),
                        );
                        if (item.score === maxScore) {
                          return (
                            <Card
                              mb='xxs'
                              backgroundColor='primary/inactive'
                              key={item.metric?.name}
                              px='xs'
                              py='xxxs'
                              width='fit-content'
                            >
                              <Typography variant='p2/light' fontSize='xs'>
                                {item.metric?.name}
                              </Typography>
                            </Card>
                          );
                        }
                        return null;
                      })}
                    </Box>
                  </Box>
                  <Box
                    mt={genericResponsiveValue({
                      breakPoints: { desktop: 'none' },
                      defaultValue: 'xs',
                    })}
                    flex='1'
                  >
                    <Typography variant='h4'>{t('insightBox.conTitle')}</Typography>
                    <Box display='flex' flexDirection='column' mt='xs'>
                      {survey.results?.results?.map(item => {
                        const minScore = Math.min(
                          ...(survey.results?.results ?? []).map(m => m.score ?? 0),
                        );
                        if (item.score === minScore) {
                          return (
                            <Card
                              mb='xxs'
                              backgroundColor='error/opacity'
                              key={item.metric?.name}
                              px='xs'
                              py='xxxs'
                              width='fit-content'
                            >
                              <Typography variant='p2/light' fontSize='xs'>
                                {item.metric?.name}
                              </Typography>
                            </Card>
                          );
                        }
                        return null;
                      })}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Box>
          ),
        )}
    </Box>
  );
};
